import {
  AssignmentOutlined,
  CheckCircleOutline,
  ScheduleOutlined,
  WarningAmberOutlined
} from '@mui/icons-material';
import { Box, Button, Chip, Grid, SelectChangeEvent, styled, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { TableCustom } from 'app/components/elements/TableCustom';
import { InputSelect } from 'app/components/elements/InputSelect';
import { CriticalityChip } from 'app/pages/IssuesPage/components/CriticalityChip';
import { TAB_ISSUES } from 'app/pages/IssuesPage/const';
import { MaintenanceStatus } from 'services/maintenanceEngine';
import { DEFAULT_CRITICALITY_LEVELS, getCriticalityDisplay } from 'utils/issueCriticality';
import { MaintenanceTabType } from '../../../hook';
import { STATUS_COLORS } from '../shared/maintenanceTable';
import { completedMaintenanceStatusChip } from '../CompletedMaintanence/completedMaintenanceTable';
import { RECENTLY_COMPLETED_WINDOW_DAYS, useDashboardInjection } from './hook';

const DEVICE_PREVIEW_HEADERS = [
  { label: 'Asset Name', width: 220 },
  { label: 'Location', width: 180 },
  { label: 'Remaining Frequency', width: 160 },
  { label: 'Trigger Type', width: 180 },
  { label: 'Status', width: 140 }
];

const SENSOR_PREVIEW_HEADERS = [
  { label: 'Asset Name', width: 220 },
  { label: 'IoT Device', width: 200 },
  { label: 'Live Value', width: 140 },
  { label: 'Trigger Type', width: 180 },
  { label: 'Status', width: 140 }
];

const COMPLETED_PREVIEW_HEADERS = [
  { label: 'Asset Name', width: 200 },
  { label: 'Client Name', width: 180 },
  { label: 'Location', width: 180 },
  { label: 'Assigned To', width: 160 },
  { label: 'Priority', width: 110 },
  { label: 'Status', width: 120 }
];

const CURRENT_TASKS_HEADERS = [
  { label: 'Asset Name', width: 200 },
  { label: 'Location', width: 180 },
  { label: 'Assigned To', width: 160 },
  { label: 'Due Date', width: 130 },
  { label: 'Priority', width: 110 },
  { label: 'Status', width: 120 }
];

const ASSIGNMENTS_HEADERS = [
  { label: 'Technician', width: 220 },
  { label: 'Active Jobs', width: 140 }
];

type StatCardConfig = {
  title: string;
  subtitle: string;
  value: number;
  icon: typeof AssignmentOutlined;
  bgcolor: string;
  color: string;
  onClick?: () => void;
};

type DashboardProps = {
  onNavigate: (tab: MaintenanceTabType, statusFilter?: MaintenanceStatus) => void;
};

const HEALTH_LEGEND_COLORS = {
  healthy: '#16A34A',
  dueSoon: '#F59E0B',
  dueToday: '#F97316',
  overdue: '#DC2626',
  offlineManual: '#9CA3AF'
};

const statusChip = (status: MaintenanceStatus) => (
  <Chip
    size="small"
    label={status}
    sx={{ ...STATUS_COLORS[status], fontWeight: 700, fontSize: 11 }}
  />
);

export const Dashboard = ({ onNavigate }: DashboardProps) => {
  const {
    stats,
    upcomingPreview,
    comingUpNextPreview,
    sensorPreview,
    calendarPreview,
    completedPreview,
    currentTasksPreview,
    assignmentsPreview,
    clientOptions,
    selectedClient,
    setSelectedClient,
    loading
  } = useDashboardInjection();
  const { health } = stats;
  const navigate = useNavigate();

  const navigateForStatus = (status: MaintenanceStatus) => {
    if (status === 'OVERDUE') return onNavigate('Maintenance History', 'OVERDUE');
    if (status === 'DUE') return onNavigate('Asset Due');
    if (status === 'UPCOMING') return onNavigate('Upcoming Maintenance');
    return onNavigate('Maintenance History', 'NORMAL');
  };

  const upcomingRows = upcomingPreview.map((device) => ({
    assetName: { value: device.assetName },
    location: { value: device.location || '-' },
    remainingHours: { value: device.remainingHours ?? '-' },
    triggerType: { value: device.maintenanceTriggerType || '-' },
    status: { value: statusChip(device.maintenanceStatus) }
  }));

  const comingUpNextRows = comingUpNextPreview.map((device) => ({
    assetName: { value: device.assetName },
    location: { value: device.location || '-' },
    remainingHours: { value: device.remainingHours ?? '-' },
    triggerType: { value: device.maintenanceTriggerType || '-' },
    status: { value: statusChip(device.maintenanceStatus) }
  }));

  const sensorRows = sensorPreview.map((device) => ({
    assetName: { value: device.assetName },
    iotDevice: { value: device.iotDeviceName || device.iotDeviceId || '-' },
    currentReading: { value: device.currentReadingValue ?? '-' },
    triggerType: { value: device.maintenanceTriggerType || '-' },
    status: { value: statusChip(device.maintenanceStatus) }
  }));

  const calendarRows = calendarPreview.map((device) => ({
    assetName: { value: device.assetName },
    location: { value: device.location || '-' },
    remainingHours: { value: device.remainingHours ?? '-' },
    triggerType: { value: device.maintenanceTriggerType || '-' },
    status: { value: statusChip(device.maintenanceStatus) }
  }));

  const completedRows = completedPreview.map((issue) => ({
    assetName: { value: issue.assetName || '-' },
    clientName: { value: issue.assetClientName || '-' },
    location: { value: issue.location || '-' },
    assignedTo: { value: issue.assignedTo?.name || '-' },
    criticality: {
      value: (
        <CriticalityChip
          criticality={getCriticalityDisplay(issue.criticalityKey, DEFAULT_CRITICALITY_LEVELS)}
        />
      )
    },
    status: { value: completedMaintenanceStatusChip(issue.status) }
  }));

  const currentTasksRows = currentTasksPreview.map((issue) => ({
    assetName: { value: issue.assetName || '-' },
    location: { value: issue.location || '-' },
    assignedTo: { value: issue.assignedTo?.name || '-' },
    dueDate: { value: issue.dueDate ? dayjs(issue.dueDate).format('YYYY-MM-DD') : '-' },
    criticality: {
      value: (
        <CriticalityChip
          criticality={getCriticalityDisplay(issue.criticalityKey, DEFAULT_CRITICALITY_LEVELS)}
        />
      )
    },
    status: { value: completedMaintenanceStatusChip(issue.status) }
  }));

  const assignmentsRows = assignmentsPreview.map((tech) => ({
    technician: { value: tech.name },
    activeJobs: { value: tech.activeJobs }
  }));

  const cards: StatCardConfig[] = [
    {
      title: 'Assets Due Today',
      subtitle: 'Due for maintenance',
      value: stats.dueToday,
      icon: ScheduleOutlined,
      bgcolor: '#FEF3C7',
      color: '#B45309',
      onClick: () => onNavigate('Asset Due')
    },
    {
      title: 'Upcoming Maintenance',
      subtitle: 'Scheduled ahead',
      value: stats.upcoming,
      icon: AssignmentOutlined,
      bgcolor: '#E0E7FF',
      color: '#4338CA',
      onClick: () => onNavigate('Upcoming Maintenance')
    },
    {
      title: 'Overdue Assets',
      subtitle: 'Past due',
      value: stats.overdue,
      icon: WarningAmberOutlined,
      bgcolor: '#FEE2E2',
      color: '#DC2626',
      onClick: () => onNavigate('Maintenance History', 'OVERDUE')
    },
    {
      title: 'Recently Completed',
      subtitle: `Last ${RECENTLY_COMPLETED_WINDOW_DAYS} days`,
      value: stats.recentlyCompleted,
      icon: CheckCircleOutline,
      bgcolor: '#DCFCE7',
      color: '#15803D',
      onClick: () => onNavigate('Maintenance History')
    },
    {
      title: 'Total Monitored Assets',
      subtitle: 'All assets',
      value: stats.totalMonitored,
      icon: AssignmentOutlined,
      bgcolor: '#DBEAFE',
      color: '#1D4ED8',
      onClick: () => onNavigate('Maintenance History')
    }
  ];

  return (
    <TabContentContainer>
      <DashboardHeaderRow>
        <Typography fontSize={24} fontWeight={700} color="#226AAA">
          Dashboard
        </Typography>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {loading && (
            <Typography fontSize={12} color="#9CA3AF">
              Refreshing…
            </Typography>
          )}
          <Box width={220}>
            <InputSelect
              placeholder="All Clients"
              value={selectedClient}
              onChange={(e: SelectChangeEvent<unknown>) =>
                setSelectedClient((e.target.value as string) || '')
              }
              options={clientOptions}
            />
          </Box>
        </Stack>
      </DashboardHeaderRow>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} lg={1.8} key={card.title}>
            <StatCard onClick={card.onClick} clickable={Boolean(card.onClick)}>
              <StatCardHeader>
                <IconWrapper sx={{ bgcolor: card.bgcolor, color: card.color }}>
                  <card.icon fontSize="small" />
                </IconWrapper>
                <Typography fontSize={12} fontWeight={700} color="#374151">
                  {card.title}
                </Typography>
              </StatCardHeader>
              <Typography fontSize={26} fontWeight={800} color="#111827">
                {card.value}
              </Typography>
              <Typography fontSize={11} color="#9CA3AF">
                {card.subtitle}
              </Typography>
            </StatCard>
          </Grid>
        ))}

        <Grid item xs={12} sm={12} lg={3} key="health">
          <HealthCard>
            <Typography fontSize={13} fontWeight={700} color="#111827">
              Overall Maintenance Health
            </Typography>
            <HealthContent>
              <HealthDonut
                healthy={health.healthy}
                dueSoon={health.dueSoon}
                dueToday={health.dueToday}
                overdue={health.overdue}
                offlineManual={health.offlineManual}
                healthPercent={health.healthPercent}
              />
              <HealthLegend>
                <LegendItem
                  color={HEALTH_LEGEND_COLORS.healthy}
                  label="Healthy"
                  value={health.healthy}
                  onClick={() => onNavigate('Maintenance History', 'NORMAL')}
                />
                <LegendItem
                  color={HEALTH_LEGEND_COLORS.dueSoon}
                  label="Due Soon"
                  value={health.dueSoon}
                  onClick={() => onNavigate('Upcoming Maintenance')}
                />
                <LegendItem
                  color={HEALTH_LEGEND_COLORS.dueToday}
                  label="Due Today"
                  value={health.dueToday}
                  onClick={() => onNavigate('Asset Due')}
                />
                <LegendItem
                  color={HEALTH_LEGEND_COLORS.overdue}
                  label="Overdue"
                  value={health.overdue}
                  onClick={() => onNavigate('Maintenance History', 'OVERDUE')}
                />
                <LegendItem
                  color={HEALTH_LEGEND_COLORS.offlineManual}
                  label="Offline / Manual"
                  value={health.offlineManual}
                />
              </HealthLegend>
            </HealthContent>
          </HealthCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Upcoming Maintenance
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigate('Upcoming Maintenance')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && upcomingRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No upcoming maintenance at this time.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={() => onNavigate('Upcoming Maintenance')}
                  headers={DEVICE_PREVIEW_HEADERS}
                  data={upcomingRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                What&apos;s Coming Up Next
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigate('Maintenance History')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && comingUpNextRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    Nothing needs attention right now.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={(index) =>
                    navigateForStatus(comingUpNextPreview[index].maintenanceStatus)
                  }
                  headers={DEVICE_PREVIEW_HEADERS}
                  data={comingUpNextRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Sensor-Based Maintenance Recommendations
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigate('Maintenance History')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && sensorRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No sensor-monitored assets found.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={(index) =>
                    navigateForStatus(sensorPreview[index].maintenanceStatus)
                  }
                  headers={SENSOR_PREVIEW_HEADERS}
                  data={sensorRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Maintenance Calendar
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigate('Maintenance History')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && calendarRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No calendar-triggered assets found.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={(index) =>
                    navigateForStatus(calendarPreview[index].maintenanceStatus)
                  }
                  headers={DEVICE_PREVIEW_HEADERS}
                  data={calendarRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Current Tasks
              </Typography>
              <Button
                size="small"
                onClick={() => navigate(`/issues?tab=${TAB_ISSUES.MAINTENANCE_ISSUES}`)}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && currentTasksRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No active maintenance tasks found.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={() => navigate(`/issues?tab=${TAB_ISSUES.MAINTENANCE_ISSUES}`)}
                  headers={CURRENT_TASKS_HEADERS}
                  data={currentTasksRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Maintenance Assignments
              </Typography>
              <Button
                size="small"
                onClick={() => navigate(`/issues?tab=${TAB_ISSUES.ASSIGNMENTS}`)}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && assignmentsRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No technician assignments found.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={() => navigate(`/issues?tab=${TAB_ISSUES.ASSIGNMENTS}`)}
                  headers={ASSIGNMENTS_HEADERS}
                  data={assignmentsRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <PreviewCard>
            <PreviewHeader>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                Completed Maintenance
              </Typography>
              <Button
                size="small"
                onClick={() => onNavigate('Completed Maintenance')}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                View all
              </Button>
            </PreviewHeader>
            <PreviewBody>
              {!loading && completedRows.length === 0 ? (
                <PreviewEmptyState>
                  <Typography color="#6B7280" fontSize={14}>
                    No completed maintenance records found.
                  </Typography>
                </PreviewEmptyState>
              ) : (
                <TableCustom
                  handleChooseRow={() => onNavigate('Completed Maintenance')}
                  headers={COMPLETED_PREVIEW_HEADERS}
                  data={completedRows}
                  isScrollX
                />
              )}
            </PreviewBody>
          </PreviewCard>
        </Grid>
      </Grid>
    </TabContentContainer>
  );
};

type HealthDonutProps = {
  healthy: number;
  dueSoon: number;
  dueToday: number;
  overdue: number;
  offlineManual: number;
  healthPercent: number;
};

const DONUT_SIZE = 108;
const DONUT_STROKE_WIDTH = 13;

const HealthDonut = ({
  healthy,
  dueSoon,
  dueToday,
  overdue,
  offlineManual,
  healthPercent
}: HealthDonutProps) => {
  const total = healthy + dueSoon + dueToday + overdue + offlineManual;
  const center = DONUT_SIZE / 2;
  const radius = center - DONUT_STROKE_WIDTH / 2 - 2;
  const circumference = 2 * Math.PI * radius;

  const segments = [
    { value: healthy, color: HEALTH_LEGEND_COLORS.healthy },
    { value: dueSoon, color: HEALTH_LEGEND_COLORS.dueSoon },
    { value: dueToday, color: HEALTH_LEGEND_COLORS.dueToday },
    { value: overdue, color: HEALTH_LEGEND_COLORS.overdue },
    { value: offlineManual, color: HEALTH_LEGEND_COLORS.offlineManual }
  ];

  let offset = 0;

  return (
    <DonutWrapper>
      <svg width={DONUT_SIZE} height={DONUT_SIZE} viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#F3F4F6"
          strokeWidth={DONUT_STROKE_WIDTH}
        />
        {total > 0 &&
          segments.map((segment, index) => {
            if (segment.value <= 0) return null;
            const length = (segment.value / total) * circumference;
            const dashoffset = -offset;
            offset += length;
            return (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={DONUT_STROKE_WIDTH}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={dashoffset}
                transform={`rotate(-90 ${center} ${center})`}
                strokeLinecap="butt"
              />
            );
          })}
      </svg>
      <DonutCenter>
        <Typography fontSize={22} fontWeight={800} color="#111827">
          {healthPercent}%
        </Typography>
        <Typography fontSize={11} color="#6B7280">
          Healthy
        </Typography>
      </DonutCenter>
    </DonutWrapper>
  );
};

type LegendItemProps = {
  color: string;
  label: string;
  value: number;
  onClick?: () => void;
};

const LegendItem = ({ color, label, value, onClick }: LegendItemProps) => (
  <LegendRow onClick={onClick} clickable={Boolean(onClick)}>
    <Dot sx={{ bgcolor: color }} />
    <Typography fontSize={11} color="#374151" flex={1} noWrap>
      {label}
    </Typography>
    <Typography fontSize={11} fontWeight={700} color="#111827">
      {value}
    </Typography>
  </LegendRow>
);

const TabContentContainer = styled(Stack)(() => ({
  flex: 1,
  gap: 20,
  paddingTop: 40,
  paddingBottom: 40
}));

const DashboardHeaderRow = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 12
}));

const StatCard = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'clickable'
})<{ clickable?: boolean }>(({ clickable }) => ({
  gap: 4,
  padding: '14px',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  height: '100%',
  cursor: clickable ? 'pointer' : 'default',
  transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
  '&:hover': clickable
    ? { boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderColor: '#D1D5DB' }
    : undefined
}));

const StatCardHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8
}));

const IconWrapper = styled(Box)(() => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}));

const PreviewCard = styled(Stack)(() => ({
  gap: 12,
  padding: '20px',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  height: '100%'
}));

const PreviewHeader = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const PREVIEW_BODY_MIN_HEIGHT = 296;

const PreviewBody = styled(Stack)(() => ({
  flex: 1,
  minHeight: PREVIEW_BODY_MIN_HEIGHT
}));

const PreviewEmptyState = styled(Stack)(() => ({
  flex: 1,
  minHeight: PREVIEW_BODY_MIN_HEIGHT,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center'
}));

const HealthCard = styled(Stack)(() => ({
  gap: 2,
  padding: '14px',
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  height: '100%'
}));

const HealthContent = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'nowrap'
}));

const DonutWrapper = styled(Box)(() => ({
  position: 'relative',
  width: DONUT_SIZE,
  height: DONUT_SIZE,
  flexShrink: 0
}));

const DonutCenter = styled(Stack)(() => ({
  position: 'absolute',
  inset: 0,
  alignItems: 'center',
  justifyContent: 'center'
}));

const HealthLegend = styled(Stack)(() => ({
  gap: 4,
  flex: 1,
  minWidth: 0
}));

const LegendRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'clickable'
})<{ clickable?: boolean }>(({ clickable }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  cursor: clickable ? 'pointer' : 'default',
  borderRadius: 6,
  padding: '2px 4px',
  transition: 'background-color 0.15s ease',
  '&:hover': clickable ? { backgroundColor: '#F9FAFB' } : undefined
}));

const Dot = styled(Box)(() => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0
}));
